import axios from "axios";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { capitalizeFirstLetter, getDisplayName } from "../utils/tools";
import styles from "../styles/Model.module.css";
import { Heart } from "react-iconly";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@jammable/ui-components/Accordion";
import { toast } from "react-hot-toast";
import { getAuth } from "firebase/auth";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { auth } from "../utils/firebaseAdmin";
import millify from "millify";
import { useModal } from "../context/ModalContext";
import { useAuth } from "../context/AuthProvider";
import useAuthModal from "../hooks/useAuthModal";
import { AiFillLock } from "react-icons/ai";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Model from "../models/Model";
import { IoIosRemoveCircle } from "react-icons/io";
import {
  FaComment,
  FaPlus,
  FaSearch,
  FaShare,
  FaStar,
  FaTrash,
} from "react-icons/fa";
import { HiCursorClick } from "react-icons/hi";
import { useQuery } from "react-query";
import { ModelGrid } from "@jammable/ui-components/ModelGrid";
import { BsCollectionFill, BsThreeDots } from "react-icons/bs";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { MdEdit, MdThumbsUpDown } from "react-icons/md";
import { Spinner } from "@jammable/ui-components/Loader";
import { Button, SecondaryButton } from "@jammable/ui-core";
import Dropdown from "../components/dropdown/Dropdown";
import VoiceImage from "@/components/VoiceImage";
import connectToDb from "@/utils/mongodb";
import dynamic from "next/dynamic";
import { MODEL_PROJECTION } from "./api/model";
import { IoArrowForward, IoSparkles } from "react-icons/io5";
import Image from "next/image";
import CreateModal from "@/components/modals/CreateModal";

// Dynamically import all modals to reduce initial bundle size

const EditModelModal = dynamic(
  () => import("../components/modals/EditModelModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const DeleteModelModal = dynamic(
  () => import("../components/modals/DeleteModelModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const ReportModal = dynamic(() => import("../components/modals/ReportModal"), {
  ssr: false,
  loading: () => (
    <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
      <Spinner size={8} className="tw-border-3" />
    </div>
  ),
});
const AddToCollectionModal = dynamic(
  () => import("../components/collection/modals/AddToCollectionModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const CreateCollectionModal = dynamic(
  () => import("@/components/collection/modals/CreateCollectionModal"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);
const ModelCollectionsWithVoice = dynamic(
  () => import("@/components/ModelCollectionsWithVoice"),
  {
    ssr: false,
    loading: () => (
      <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
        <Spinner size={8} className="tw-border-3" />
      </div>
    ),
  },
);

// Lazy load heavy sections that are below the fold
const LazyTryVoiceSection = dynamic(
  () => import("../components/TryVoiceSection"),
  {
    ssr: true,
    loading: () => (
      <div className="tw-h-96 tw-animate-pulse tw-bg-gray-100 tw-rounded-xl" />
    ),
  },
);
const LazyModelCommentSection = dynamic(
  () => import("../components/ModelCommentSection"),
  {
    ssr: true,
    loading: () => (
      <div className="tw-h-64 tw-animate-pulse tw-bg-gray-100 tw-rounded-xl tw-mt-8" />
    ),
  },
);
const LazyLibraryDemosSection = dynamic(
  () => import("@/components/LibraryDemosSection"),
  {
    ssr: true,
    loading: () => (
      <div className="tw-h-96 tw-animate-pulse tw-bg-gray-100 tw-rounded-xl" />
    ),
  },
);

const ModelPage = ({ modelData, isPrivate, isRemoved, token }) => {
  // Initialize dayjs plugin once at module level
  if (!dayjs?.plugins?.relativeTime) {
    dayjs.extend(relativeTime);
  }

  const { user } = useAuth();
  const { checkAuthAndExecute } = useAuthModal();

  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const [isLiked, setIsLiked] = useState(modelData?.isLiked);
  const [likes, setLikes] = useState(modelData?.likes ? modelData?.likes : 0);

  const { openModal, closeModal } = useModal();

  const title = `${modelData?.artistname} AI Voice Generator | Jammable AI Covers`;
  const description = `${modelData?.artistname} AI voice & song generator. Create ${modelData?.artistname} AI voice covers with advanced AI voice technology and 50,000+ AI voices.`;
  const url = `https://www.jammable.com/${modelData?.urlParam}`;

  // Memoize expensive color calculations
  const { vibrantColor, darkColor, textColor, buttonStyle } = useMemo(() => {
    const vibrantColorRgb = modelData?.image?.colorPalette?.Vibrant?.rgb;

    const vibrantColor = vibrantColorRgb
      ? `rgb(${vibrantColorRgb.join(", ")})`
      : "#7e22ce";

    const darkenRgb = (rgb, amount = 40) => {
      if (!rgb) return [0, 0, 0];
      return rgb.map((c) => Math.max(0, c - amount));
    };

    const darkColorRgb = darkenRgb(vibrantColorRgb);
    const darkColor = `rgb(${darkColorRgb.join(", ")})`;

    const getLuminance = (rgb) => {
      if (!rgb) return 0;
      const [r, g, b] = rgb.map((c) => {
        c /= 255.0;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };

    const luminance = getLuminance(vibrantColorRgb);
    const textColor = luminance > 0.5 ? "#000000" : "#FFFFFF";

    const buttonStyle = vibrantColorRgb
      ? {
          backgroundColor: vibrantColor,
          borderColor: vibrantColor,
          borderBottomColor: darkColor,
          color: textColor,
        }
      : {};

    return { vibrantColor, darkColor, textColor, buttonStyle };
  }, [modelData?.image?.colorPalette?.Vibrant?.rgb]);

  const likeModel = async () => {
    if (!checkAuthAndExecute()) return false;
    if (isLiked) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    setIsLiked(!isLiked);
    const auth = getAuth();
    const token = await auth?.currentUser?.getIdToken();
    if (!token) {
      return;
    }
    axios
      .post(
        `/api/user/likeModel`,
        { modelId: modelData?._id },
        { headers: { Authorization: token } },
      )
      .then((res) => {
        toast.success(res?.data.message);
      })
      .catch((err) => {
        toast.success("Something went wrong. Please try again");
        console.error("Error liking model:", err);
      });
  };

  const badges = [...(modelData?.badges ?? [])].slice(0, 3);

  const similarVoiceData = useQuery(
    ["similar-voices", modelData?._id],
    async () => {
      const { data } = await axios.get(`/api/model/getDuetMatches`, {
        params: {
          modelIds: modelData?._id,
          limit: 4,
        },
      });
      return data;
    },
    {
      enabled: !!modelData?._id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const collections = useQuery(
    ["collections", modelData?._id],
    async () => {
      const { data } = await axios.get(`/api/model/getCollections`, {
        params: {
          modelId: modelData?._id,
        },
      });
      return data;
    },
    {
      enabled: !!modelData?._id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const commentSectionRef = useRef(null);
  const scrollToComments = () => {
    if (commentSectionRef.current) {
      const yOffset = -130;
      const element = commentSectionRef.current;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Memoize the demo count calculation
  const librarySongDemoSuccessCount = useMemo(() => {
    return (
      modelData?.librarySongDemos?.filter((demo) => demo.status === "processed")
        ?.length || 0
    );
  }, [modelData?.librarySongDemos]);

  return isRemoved ? (
    <div style={{ height: "100vh" }} className="container-center">
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title: title,
          description: description,
          url: url,

          images: [
            {
              url: modelData?.image?.url,
              width: 900,
              height: 900,
              alt: "Jammable Model",
            },
          ],
        }}
      />
      <div
        style={{
          color: "black",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
        className="main"
      >
        <IoIosRemoveCircle size={100} />
        <div className="tw-text-center tw-text-3xl tw-py-2 tw-font-black tw-tracking-tight">
          This voice has been removed.
        </div>
        <Button
          as={Link}
          href={`/models`}
          className="tw-rounded-full tw-mt-2"
          flat
        >
          Browse Other Voices
        </Button>
      </div>
    </div>
  ) : isPrivate ? (
    <div style={{ height: "100vh" }} className="container-center">
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title: title,
          description: description,
          url: url,

          images: [
            {
              url: modelData?.image?.url,
              width: 900,
              height: 900,
              alt: "Jammable Model",
            },
          ],
        }}
      />
      <div
        style={{
          color: "black",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
        className="main"
      >
        <AiFillLock size={100} />
        <div className="tw-text-center tw-text-3xl tw-py-2 tw-font-black tw-tracking-tight">
          This voice is private.
        </div>
        <div className="tw-flex tw-gap-2">
          <Button
            as={Link}
            href={`/models`}
            className="tw-rounded-full tw-mt-1"
          >
            Browse other Voices
          </Button>
          <SecondaryButton
            as={Link}
            href={`/login`}
            className="tw-rounded-full tw-mt-1"
          >
            Sign In
          </SecondaryButton>
        </div>
      </div>
    </div>
  ) : (
    <div className={`container ${styles.container}`}>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title: title,
          description: description,
          url: url,

          images: [
            {
              url: modelData?.image?.url,
              width: 900,
              height: 900,
              alt: "Jammable Model",
            },
          ],
        }}
      />
      <div
        style={{
          marginTop: 30,
          paddingInline: 0,
        }}
        className="main"
      >
        <div className={styles["model-page-mobile-container"]}>
          <VoiceImage
            model={modelData}
            fill
            quality={25}
            className={styles["model-page-mobile"]}
            priority={true}
            placeholder="blur"
            blurDataURL={modelData?.image?.blurDataURL}
          />
        </div>
        <div className="tw-w-full tw-px-2">
          {modelData?.newerVersion?._id && (
            <Link
              href={`/${modelData?.newerVersion?.urlParam || modelData?.newerVersion?._id}`}
              className="tw-fixed md:tw-relative tw-z-20 tw-top-[80px] md:tw-top-0 tw-left-0 tw-right-0 md:tw-mb-2 tw-mx-2 md:tw-mx-0 tw-flex tw-items-center tw-justify-between tw-bg-purple-50 hover:tw-bg-purple-100 tw-transition-all   tw-p-3 md:tw-p-4 tw-rounded-xl md:tw-rounded-2xl"
            >
              <div className="tw-flex tw-items-center tw-gap-2 md:tw-gap-4">
                <div className="tw-flex tw-items-center tw-justify-center tw-relative">
                  <div className="tw-absolute tw--top-0.5 md:tw--top-1 tw--right-0.5 md:tw--right-1 tw-bg-green-400 tw-w-2 md:tw-w-3 tw-h-2 md:tw-h-3 tw-rounded-full tw-border-2 tw-border-white" />

                  <div className="tw-w-10 tw-h-10 tw-rounded-lg md:tw-rounded-xl tw-bg-purple-200 tw-flex tw-items-center tw-justify-center">
                    <IoSparkles className="tw-text-purple-600" size={20} />
                  </div>
                </div>
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs md:tw-text-sm tw-font-black tw-text-purple-700">
                    Newer Version Available!
                  </div>
                  <div className="tw-text-[10px] md:tw-text-xs tw-text-purple-600">
                    {modelData?.username} has updated their voice. Click to try
                    the latest version.
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1 md:tw-gap-2">
                <IoArrowForward
                  className="tw-text-purple-700"
                  strokeWidth={4}
                  size={24}
                />
              </div>
            </Link>
          )}
          <div className="tw-flex tw-justify-between tw-gap-2 tw-w-full tw-p-0">
            <div className="tw-flex tw-gap-6 tw-w-full tw-relative">
              <div className="tw-hidden tw-relative md:tw-flex tw-w-max">
                <VoiceImage
                  model={modelData}
                  size={200}
                  quality={75}
                  className={`tw-hidden md:tw-flex tw-rounded-3xl`}
                  priority={true}
                  placeholder="blur"
                  blurDataURL={modelData?.image?.blurDataURL}
                />
              </div>
              <div className="tw-h-full tw-flex tw-flex-col md:tw-mt-0 tw--mt-28 tw-z-10 tw-w-full">
                <div className="md:tw-hidden  tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                  {modelData?.isCommunityFavorite && (
                    <div className=" tw-flex md:tw-hidden tw-items-center tw-justify-center tw-gap-1 tw-bg-gradient-to-r tw-from-amber-300/80 tw-to-amber-400 tw-text-black tw-px-2 tw-py-1 tw-rounded-2xl tw-text-xs tw-text-center tw-font-black ">
                      <FaStar
                        size={16}
                        className="tw-text-black tw-z-10 tw--mt-1"
                      />
                      COMMUNITY FAVORITE
                    </div>
                  )}
                  {badges?.map((badge) => {
                    return (
                      <Link
                        key={badge}
                        href={{ pathname: `/models`, query: { filter: badge } }}
                        style={{
                          whiteSpace: "nowrap",
                        }}
                        className="tw-p-1 tw-text-nowrap tw-px-2 tw-text-sm tw-flex tw-items-center tw-gap-1 tw-font-bold tw-bg-purple-500/20 hover:tw-bg-purple-200 tw-border tw-rounded-3xl "
                      >
                        <FaSearch className="tw-text-purple-800 " size={15} />
                        <span className="tw-text-purple-800  ">
                          {capitalizeFirstLetter(badge.trim())}
                        </span>
                      </Link>
                    );
                  })}
                </div>
                <div>
                  {modelData?.accessIds.length > 0 ? (
                    <Link
                      href={`/@${modelData?.username}`}
                      className="tw-text-lg tw-font-bold tw-overflow-visible tw-flex tw-items-center tw-gap-2 tw-text-sm"
                    >
                      <div className=" tw-bg-gray-200/60 hover:tw-bg-gray-200 tw-transition-all  tw-flex tw-items-center tw-gap-1 tw-w-max tw-text-sm tw-text-purple-700 tw-p-1 tw-px-1 tw-pr-2 tw-rounded-full">
                        <Image
                          src={modelData?.avatar}
                          width={20}
                          height={20}
                          alt={modelData?.username}
                          className="tw-rounded-full"
                        />
                        {modelData?.username}
                      </div>{" "}
                      <span className="tw-text-black/50">
                        {isClient && dayjs(modelData?.createdAt).fromNow()}
                      </span>
                    </Link>
                  ) : (
                    <div className="tw-text-lg tw-font-bold tw-opacity-60 md:tw-opacity-40">
                      Uploaded By{" "}
                      <span className="">{modelData?.username}</span> ·{" "}
                      <span>
                        {isClient && dayjs(modelData?.createdAt).fromNow()}
                      </span>
                    </div>
                  )}
                </div>
                <h1 className="tw-text-4xl md:tw-text-5xl tw-mb-0 tw-font-black tw-mt-2 tw-tracking-tight md:tw-truncate">
                  {getDisplayName(modelData)}
                </h1>
                <div className="tw-hidden  md:tw-flex tw-gap-2 tw-mt-4">
                  {modelData?.collection &&
                    modelData?.collection?.modelsCount > 2 && (
                      <Link
                        href={`/collection/${modelData?.collection?._id}`}
                        className="tw-text-xs tw-bg-gray-100 tw-transition-bg tw-rounded-full tw-font-bold tw-text-sm tw-flex tw-items-center tw-gap-2 tw-p-1 tw-pr-2  tw-w-max hover:tw-bg-gray-200"
                      >
                        <div className="tw-flex tw-items-center tw-justify-center ">
                          <VoiceImage
                            model={{ image: modelData?.collection?.image }}
                            size={20}
                            className="tw-rounded-xl tw-brightness-75"
                          />
                          <BsCollectionFill
                            size={8}
                            className="tw-absolute tw-white tw-text-white"
                          />
                        </div>
                        <div className="tw-flex tw-flex-col tw-gap-0">
                          <span>
                            {modelData?.collection?.modelsCount} Voices
                          </span>
                        </div>
                      </Link>
                    )}
                  {modelData?.isCommunityFavorite && (
                    <div className="md:tw-flex tw-hidden tw-items-center tw-justify-center tw-gap-1 tw-bg-gradient-to-r tw-from-amber-300/80 tw-to-amber-400 tw-text-black tw-px-2 tw-py-1 tw-rounded-2xl tw-text-xs tw-text-center tw-font-black ">
                      <FaStar
                        size={16}
                        className="tw-text-black tw-z-10 tw--mt-1"
                      />
                      COMMUNITY FAVORITE
                    </div>
                  )}
                  {badges?.map((badge) => {
                    return (
                      <Link
                        key={badge}
                        href={{ pathname: `/models`, query: { filter: badge } }}
                        style={{
                          whiteSpace: "nowrap",
                        }}
                        className="tw-p-1 tw-text-nowrap tw-px-2 tw-text-sm tw-flex tw-items-center tw-gap-1 tw-font-bold tw-bg-purple-100 hover:tw-bg-purple-200 tw-transition-bg tw-border tw-rounded-3xl "
                      >
                        <FaSearch className="tw-text-purple-700 " size={15} />
                        <span className="tw-text-purple-700  ">
                          {capitalizeFirstLetter(badge.trim())}
                        </span>
                      </Link>
                    );
                  })}
                </div>
                <div className="tw-mt-4 md:tw-mt-auto  tw-flex tw-items-center tw-gap-2">
                  <Button
                    className="tw-w-max md:-tw-px-4 tw-flex tw-min-w-max  tw-gap-1 tw-text-xl tw-h-14 tw-rounded-2xl tw-font-black tw-border tw-border-solid tw-border-b-[5px] active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out"
                    style={buttonStyle}
                    onClick={() => {
                      console.log("[Model Page] CREATE button clicked/tapped.");
                      if (!checkAuthAndExecute()) {
                        console.log(
                          "[Model Page] Auth check failed or initiated auth flow.",
                        );
                        return false;
                      }
                      console.log(
                        "[Model Page] Auth check passed. Attempting to open modal.",
                      );

                      openModal(
                        "create-modal",
                        <CreateModal initialModel={modelData} />,
                        {
                          className:
                            "md:tw-max-w-xl md:tw-min-w-[600px] tw-p-0",
                        },
                      );
                      console.log("[Model Page] openModal function called.");
                    }}
                  >
                    <HiCursorClick size={24} />
                    <div className="tw-text-lg tw-flex tw-flex-col tw-gap-0.5 tw-leading-none  tw-font-black tw-h-max">
                      CREATE
                      <span className="tw-text-[11px] tw-leading-none tw-font-semibold tw-uppercase tw-tracking-wider">
                        {millify(modelData?.uses)} Uses
                      </span>
                    </div>
                  </Button>

                  <div
                    onClick={async () => {
                      likeModel();
                    }}
                    className={`tw-flex tw-cursor-pointer tw-text-sm tw-p-2 tw-h-max tw-px-3 tw-rounded-3xl tw-transition-bg tw-items-center tw-gap-2 tw-font-bold ${isLiked ? "tw-bg-purple-100  tw-text-purple-700" : "tw-bg-gray-100 hover:tw-bg-gray-200  tw-text-gray-500"} tw-w-max`}
                  >
                    <Heart
                      size={21}
                      set={isLiked ? "bold" : "bold"}
                      className={`${isLiked ? "tw-purple-700" : "tw-gray-300"}`}
                    />
                    {likes}
                  </div>
                  <div
                    onClick={scrollToComments}
                    className={`tw-flex tw-cursor-pointer tw-text-sm tw-p-2 tw-px-3 tw-rounded-3xl tw-items-center tw-gap-2 tw-font-bold tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-bg tw-text-gray-500 tw-w-max`}
                  >
                    <FaComment
                      size={21}
                      set={isLiked ? "bold" : "bold"}
                      className={`${isLiked ? "tw-purple-700" : "tw-gray-300"}`}
                    />
                    {modelData?.comments ?? 0}
                  </div>

                  <div
                    onClick={() => {
                      if (!checkAuthAndExecute()) return false;
                      openModal(
                        "add-to-collection-modal",
                        <AddToCollectionModal
                          model={modelData}
                          reroute={true}
                          closeModal={() =>
                            closeModal("add-to-collection-modal")
                          }
                          openCreate={() => {
                            closeModal("add-to-collection-modal");

                            setTimeout(() => {
                              openModal(
                                "create-collection-modal",
                                <CreateCollectionModal />,
                              );
                            }, 100);
                          }}
                        />,
                      );
                    }}
                    className="tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-bg tw-relative tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-p-3 tw-text-gray-500"
                  >
                    <FaPlus className="tw-z-10" />
                  </div>

                  <Dropdown
                    dropdownItems={
                      <>
                        <DropdownMenuItem
                          onClick={async () => {
                            const textToCopy = `https://www.jammable.com/${modelData?.urlParam}`;
                            navigator.clipboard.writeText(textToCopy);
                            toast.success("Copied to clipboard!");
                          }}
                          className="tw-p-2 tw-pl-3 tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                        >
                          <FaShare />
                          Share Voice
                        </DropdownMenuItem>
                        {isClient &&
                          modelData?.accessIds.includes(user?.firebaseUid) && (
                            <DropdownMenuItem
                              onClick={() => {
                                openModal(
                                  "edit-model-modal",
                                  <EditModelModal
                                    model={modelData}
                                    reroute={true}
                                  />,
                                );
                              }}
                              className="tw-p-2 tw-pl-3 tw-gap-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                            >
                              <MdEdit />
                              Edit Voice
                            </DropdownMenuItem>
                          )}

                        {isClient &&
                          !modelData?.accessIds.includes(user?.firebaseUid) && (
                            <DropdownMenuItem
                              onClick={() => {
                                if (!checkAuthAndExecute()) return false;
                                openModal(
                                  "report-modal",
                                  <ReportModal
                                    item={modelData}
                                    type="voice"
                                    closeModal={() =>
                                      closeModal("report-modal")
                                    }
                                  />,
                                );
                              }}
                              className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                            >
                              <FaTrash />
                              <span>Report Voice</span>
                            </DropdownMenuItem>
                          )}
                        {isClient &&
                          modelData?.accessIds.includes(user?.firebaseUid) && (
                            <DropdownMenuItem
                              onClick={() => {
                                openModal(
                                  "delete-conversion-modal",
                                  <DeleteModelModal
                                    model={modelData}
                                    closeModal={() =>
                                      closeModal("delete-model-modal")
                                    }
                                  />,
                                );
                              }}
                              className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                            >
                              <FaTrash />
                              <span>Delete Voice</span>
                            </DropdownMenuItem>
                          )}
                      </>
                    }
                  >
                    <div className="tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-bg tw-relative tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-p-2 tw-text-gray-500">
                      <BsThreeDots size={24} />
                    </div>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
          {modelData?.description && (
            <div className="tw-font-black  tw-px-4 tw-w-full tw-text-pretty tw-relative tw-border-0 tw-bg-gray-50 tw-border-solid md:tw-w-max md:tw-max-w-full tw-flex tw-flex-col tw-mt-4 tw-text-lg  tw-p-3 tw-rounded-xl">
              <div className="tw-absolute tw-shadow-lg  tw-bg-purple-100 tw-rounded-xl tw--top-2 tw--right-2 tw-text-lg tw-w-10 tw-h-10 tw-text-center tw-flex tw-p-1 tw-items-center tw-justify-center">
                📣
              </div>{" "}
              <div className="tw-pr-6">Description</div>
              <div className="tw-font-semibold tw-text-base tw-text-wrap">
                {modelData?.description}
              </div>
            </div>
          )}

          <div className="tw-mt-8 tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">
            {librarySongDemoSuccessCount > 1000 ? (
              <LazyLibraryDemosSection modelData={modelData} />
            ) : (
              <LazyTryVoiceSection modelData={modelData} />
            )}
          </div>
          <div ref={commentSectionRef}>
            <LazyModelCommentSection
              user={user}
              model={modelData}
              token={token}
            />
          </div>

          {similarVoiceData?.isLoading && !similarVoiceData?.isError ? (
            <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-20">
              <Spinner size={5} className="tw-border-3" />
            </div>
          ) : similarVoiceData?.data?.models?.length === 0 ||
            similarVoiceData?.isError ? null : (
            <div className=" tw-flex tw-mt-14 tw-w-full tw-flex tw-flex-col">
              <div className="tw-font-bold tw-text-xl tw-w-full">
                Voices Like This{" "}
              </div>
              <div className="tw-font-semibold tw-mt-1 tw-opacity-40 tw-text-sm">
                Users commonly used these voices with {modelData?.artistname}.
              </div>
              <div className="tw-w-full tw-flex tw-gap-10 tw-mt-3 tw-items-center tw-justify-center">
                {similarVoiceData?.data?.models ? (
                  <ModelGrid items={similarVoiceData?.data?.models} />
                ) : (
                  <Spinner size={5} className="tw-border-3" />
                )}
              </div>
            </div>
          )}
          {collections?.data?.collections?.length > 0 && (
            <ModelCollectionsWithVoice
              collections={collections?.data?.collections}
            />
          )}
          <div className="tw-font-bold tw-text-xl tw-w-full tw-mb-3 tw-mt-16">
            Frequently Asked Questions{" "}
          </div>
          <Accordion
            type="single"
            collapsible
            className="tw-w-full tw-mt-8 tw-border tw-border-solid tw-rounded-3xl tw-p-2 tw-px-4"
          >
            <AccordionItem value="item-4">
              <AccordionTrigger>{`How do I make ${modelData?.artistname} AI covers?`}</AccordionTrigger>
              <AccordionContent>
                <div>
                  In order to make {modelData?.artistname} AI covers, you need
                  to drop your original vocals/song into the box (please read
                  our{" "}
                  <Link
                    style={{
                      fontWeight: 600,
                      color: "#782dc8",
                      textDecoration: "underline",
                    }}
                    href="/dmca-policy"
                    target="_blank"
                  >
                    DMCA Policy
                  </Link>
                  ). Once you&apos;ve done so, press ready to convert! Your{" "}
                  {modelData?.artistname} AI cover will be ready in around 30-60
                  seconds.
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger>{`What input can I use for my ${modelData?.artistname} AI covers?`}</AccordionTrigger>
              <AccordionContent>
                <div>
                  You can only upload your original vocals/song. You should not
                  input any sound recordings that contain third party copyright
                  material without the owner&apos;s permission.
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger>{`How long will my ${modelData?.artistname} AI cover take?`}</AccordionTrigger>
              <AccordionContent>
                <div>
                  Your {modelData?.artistname} AI cover will be ready in around
                  30-60 seconds!
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7">
              <AccordionTrigger>{`How many credits does each ${modelData?.artistname} AI cover use?`}</AccordionTrigger>
              <AccordionContent>
                <div>
                  Each {modelData?.artistname} AI cover will use just 1 credit!
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8">
              <AccordionTrigger>
                Do I need to upload an acapella?
              </AccordionTrigger>
              <AccordionContent>
                <div>
                  No, you do not need to upload an acapella as our system will
                  extract the acapella regardless for your{" "}
                  {modelData?.artistname} AI cover!
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ModelPage);

export async function getServerSideProps(context) {
  const { model } = context.query;
  const { req } = context;
  const token = context?.req?.cookies?.token;

  // Add cache headers for better CDN caching
  context.res.setHeader(
    "Cache-Control",
    "public, s-maxage=60, stale-while-revalidate=300",
  );

  // Move database connection to only when needed
  let firebaseUid;
  if (token) {
    try {
      const decodedToken = await auth.verifyIdToken(token);
      firebaseUid = decodedToken.uid;
    } catch (error) {
      // Token is invalid, but continue without auth
    }
  }

  const getModelData = async (urlParam) => {
    // Only connect to DB when actually fetching data
    await connectToDb();

    // Optimize the aggregation pipeline
    let pipeline = [
      {
        $match: { urlParam: urlParam },
      },
      // Add early projection to reduce data transfer
      {
        $project: {
          ...MODEL_PROJECTION,
          isLiked: {
            $in: [firebaseUid || null, { $ifNull: ["$likedBy", []] }],
          },
          baseName: 1,
          demos: 1,
          username: "$user.username",
          avatar: "$user.avatar",
          collection: 1,
        },
      },
    ];

    // Use findOne with lean() for better performance
    const [model] = await Model.aggregate(pipeline).allowDiskUse(true).exec();
    return model;
  };

  try {
    // Early return for missing model parameter
    if (!model) {
      return { notFound: true };
    }

    const modelData = await getModelData(model);

    // Handle not found case
    if (!modelData) {
      return { notFound: true };
    }

    // Handle training models in production
    if (modelData?.training === true && process.env.NODE_ENV === "production") {
      return {
        redirect: { destination: "/models", permanent: false },
      };
    }

    // Handle private models
    if (modelData?.private && !modelData?.accessIds?.includes(firebaseUid)) {
      return {
        props: {
          isPrivate: true,
          modelData: JSON.parse(JSON.stringify(modelData)),
        },
      };
    }

    // Handle removed/deleted models
    if (modelData?.isRemoved || modelData?.deleted) {
      return {
        props: {
          isRemoved: true,
          modelData: JSON.parse(JSON.stringify(modelData)),
        },
      };
    }

    // Optimize data serialization - remove unnecessary fields
    const optimizedModelData = {
      ...modelData,
      // Remove large arrays if not needed on initial load
      likedBy: undefined,
      // Keep only necessary demo data
      librarySongDemos: modelData.librarySongDemos?.map((demo) => ({
        status: demo.status,
      })),
    };

    return {
      props: {
        modelData: JSON.parse(JSON.stringify(optimizedModelData)),
        token: token ?? null,
      },
    };
  } catch (error) {
    console.error("Error fetching model data:", error);

    // Return 404 for database errors to avoid exposing internal errors
    return {
      notFound: true,
    };
  }
}
